'use client'

import { useEffect, useMemo } from 'react'
import { errorTracker } from '@/lib/monitoring/error-tracking'

/**
 * Route-level error boundary. Renders a user-facing surface with a
 * referenceable ID; never renders raw `error.message` / `error.stack`
 * to end users (exposes internal paths, module names, line numbers —
 * a finding from the 2026-04-21 production-readiness audit).
 *
 * The full error still flows to `errorTracker.captureException`, so
 * the operator-side error sink (`error_issues` / `error_occurrences`
 * tables, admin viewer at `/admin/errors`) has everything it needs.
 *
 * Removed in this rewrite:
 *   - `<script src="//cdn.jsdelivr.net/npm/eruda">` injection. Loading
 *     a third-party devtools script into the error surface is a
 *     supply-chain + CSP violation and exposes full client state to
 *     end users in production.
 *   - `error.message`, `error.stack`, `JSON.stringify(error)` output.
 */
export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  useEffect(() => {
    errorTracker.captureException(error, {
      component: 'ErrorBoundary',
      digest: error.digest,
    })
  }, [error])

  const reference = useMemo(() => computeReference(error), [error])

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
      <div className="max-w-md w-full text-center">
        <div className="mb-8">
          <div className="mx-auto w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mb-4">
            <svg
              className="w-8 h-8 text-red-600 dark:text-red-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Something went wrong
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            An unexpected error occurred. Our team has been notified and is working on a fix.
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-500 font-mono">
            Reference: <span className="select-all">{reference}</span>
          </p>
        </div>

        <div className="space-y-3">
          <button
            onClick={reset}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try again
          </button>
          <button
            onClick={() => (window.location.href = '/dashboard')}
            className="w-full px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
          >
            Go to dashboard
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * Short, stable, URL-safe reference users can quote when contacting
 * support. Prefers Next's server-generated `digest` (already a hash);
 * falls back to an 8-char djb2 hash of the message when the error is
 * client-only and has no digest. Deterministic: the same error always
 * produces the same reference, so support-lookup by the reference
 * can correlate with the sink-side row.
 */
function computeReference(error: Error & { digest?: string }): string {
  if (error.digest && error.digest.length > 0) {
    return error.digest.slice(0, 16).toLowerCase()
  }
  const msg = error.message || 'unknown'
  let hash = 5381
  for (let i = 0; i < msg.length; i++) {
    hash = ((hash << 5) + hash + msg.charCodeAt(i)) | 0
  }
  return `e-${(hash >>> 0).toString(16).padStart(8, '0')}`
}
